// src/pages/GatherDeepInsights.js
import React from 'react';
import FeaturePageLayout from '../components/FeaturePageLayout';

// Import Icons and Images
import { 
    IoBarChartOutline,
    IoPeopleCircleOutline,
    IoBulbOutline,
    IoPodiumOutline,
    IoDocumentTextOutline
} from 'react-icons/io5';
import image1 from '../assets/images/Analytics.png';
import image2 from '../assets/images/EngagementRate.png';
import image3 from '../assets/images/Competitor.png';
import image4 from '../assets/images/Analytics2.png';
import heroBg from '../assets/images/gather-insights-background.png'; 

// --- Structured Data Schemas ---
const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{
    "@type": "ListItem",
    "position": 1,
    "name": "Home",
    "item": "https://oolook.in/"
  },{
    "@type": "ListItem",
    "position": 2,
    "name": "Gather Deep Insights",
    "item": "https://oolook.in/gather-deep-insights"
  }]
};

const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Social Media Analytics",
    "provider": { "@type": "Organization", "name": "Oolook" },
    "name": "AI-Powered Social Media Insights & Analytics",
    "description": "Oolook's analytics platform helps you gather deep insights into audience behavior, content performance, and competitor strategies to make smarter, data-driven decisions.",
    "url": "https://oolook.in/gather-deep-insights"
};

// --- Page-Specific Data ---
const gatherInsightsPageData = {
  seo: {
    title: "Gather Deep Social Media Insights with AI | Oolook",
    description: "Understand your audience, track performance, and benchmark against competitors. Oolook's AI analytics turns complex data into clear, actionable insights for growth.",
    image: 'https://oolook.in/og/gather-insights.png',
    schemas: [breadcrumbSchema, serviceSchema]
  },
  hero: {
    icon: IoBarChartOutline,
    title: "Data-Driven Decisions, Made Simple",
    subtitle: "Stop guessing what works. Oolook's AI analyzes your performance, audience, and competitors to deliver deep insights that fuel a smarter social media strategy.",
    backgroundImage: heroBg
  },
  featureSection: {
    title: "The Story Behind Your Numbers",
    subtitle: "We turn raw data into a clear narrative, showing you exactly what's driving your growth and where to focus your efforts.",
    features: [
      { title: "Unified Performance Dashboard", description: "Monitor all your key metrics across every social platform from one clean, centralized dashboard.", image: image1 },
      { title: "Deep Audience Insights", description: "Understand your audience's demographics, interests, and online behavior to create content that truly connects.", image: image2 },
      { title: "Competitor Benchmarking", description: "See how your strategy stacks up against the competition and identify opportunities to outperform them.", image: image3 },
      { title: "Customizable, Beautiful Reports", description: "Generate and schedule stunning, easy-to-understand reports to share with your team or clients.", image: image4 }
    ]
  },
  benefitGrid: {
    title: "The Strategic Advantage of True Insight",
    benefits: [
      { icon: IoBulbOutline, title: "Optimize Your Strategy", description: "Identify your best-performing content and campaigns to double down on what works." },
      { icon: IoPeopleCircleOutline, title: "Know Your Audience", description: "Build a stronger community by deeply understanding what your followers want to see." },
      { icon: IoPodiumOutline, title: "Outsmart Competitors", description: "Gain a competitive edge by learning from their successes and failures." },
      { icon: IoDocumentTextOutline, title: "Prove Your Value", description: "Use clear data and beautiful reports to demonstrate the ROI of your social media efforts." }
    ]
  },
  stepByStep: {
    title: "Uncover Insights in Minutes",
    subtitle: "Our platform is designed to deliver powerful analytics without the complexity.",
    steps: [
      { title: "1. Connect Your Accounts", description: "Oolook securely syncs your data, analyzing both historical and real-time performance." },
      { title: "2. Explore Your Dashboard", description: "Our AI automatically surfaces key trends and metrics in a clean, interactive dashboard." },
      { title: "3. Set Up Custom Reports", description: "Choose the metrics that matter most to you and schedule automated reports to be sent to your inbox." },
      { title: "4. Act with Confidence", description: "Use your newfound insights to build a more effective, data-driven social media strategy." }
    ]
  },
  targetAudience: {
    title: "Essential Insights for Every Strategist"
  }
};

const GatherDeepInsights = () => {
  return (
    <FeaturePageLayout pageData={gatherInsightsPageData} />
  );
};

export default GatherDeepInsights;